import { RouteLocationNormalized } from 'vue-router'
import { useAppStore } from '@/modules/app/app.store'
import { useProfileStore } from '@/modules/profile/profile.store'
import { identifyFrillUser } from '@/plugins/frill'

export const beforeEach = async (
  to: RouteLocationNormalized,
  errorHandler: any,
): Promise<boolean | void> => {
  // Clear errors
  useAppStore().error = null

  // Save accountID into store
  const accountID = to.params.accountId as string
  useAppStore().accountID = accountID
  const notificationIntervalID = useAppStore().notificationIntervalID

  // Clear notifications if no account ID
  if (!accountID && notificationIntervalID) {
    useAppStore().notificationIntervalID = null
    clearInterval(notificationIntervalID)
  }

  // If we're in an account...
  if (accountID && !isAccountMembershipAgnostic(to)) {
    // Set user profile in store if not there (e.g. first refresh)...
    if (!useProfileStore().data.id) {
      useAppStore().isLoading = true
      try {
        // Store PROFILE DATA
        await useProfileStore().profileFetch()
      } catch (error) {
        errorHandler.emit(error, {})
        if (error.status !== 402) {
          useAppStore().isLoading = false
        }
      }
    }

    // If non-AO is trying to access an AO route, send to forbidden screen...
    if (isAdminOwnerOnly(to) && !useProfileStore().isAdminOrOwner) {
      useAppStore().isLoading = false
      useAppStore().error = 403
      return false
    }

    if (!useProfileStore().data.profileMetrics) {
      try {
        // Store METRICS data and push info to GTM
        await useProfileStore().profileMetricsFetch()

        const profile = useProfileStore().data
        const profileMetrics = useProfileStore().data.profileMetrics

        // Frill: not while an admin is impersonating someone, which would sign them in to Frill as that person
        if (profileMetrics?.userData?.email && !profile.isImpersonating) {
          identifyFrillUser({ email: profileMetrics.userData.email, name: profile.name })
        }

        if (
          profileMetrics &&
          profileMetrics.userData &&
          profileMetrics.userData.accountMembershipIdentifier
        ) {
          // Appcues identification
          // @ts-ignore
          window.Appcues.identify(
            profileMetrics.userData.accountMembershipIdentifier,
            {
              ...profileMetrics.userData,
            },
          )

          // GTM: Add data layer vars
          // @ts-ignore
          window.dataLayer.push({
            userId: profileMetrics.userData.accountMembershipIdentifier,
            email: profileMetrics.userData.email,
            createDateUser: profileMetrics.userData.createDateUser,
            createdDateAccount: profileMetrics.userData.createdDateAccount,
            accountUserAddedDate: profileMetrics.userData.accountUserAddedDate,
            companyName: profileMetrics.userData.company,
            companyIndustry: profileMetrics.userData.companyIndustry,
            companySize: profileMetrics.userData.companySize,
            accountName: profileMetrics.userData.accountName,
            accountId: profileMetrics.userData.accountID,
            accountTeamTotal: profileMetrics.userData.accountTeamTotal,
            accountCompanyTotal: profileMetrics.userData.accountCompanyTotal,
            accountProjectTotal: profileMetrics.userData.accountProjectTotal,
            accountMailboxTotal: profileMetrics.userData.accountMailboxTotal,
            accountTaskTotal: profileMetrics.userData.accountTaskTotal,
            accountConversationTotal:
              profileMetrics.userData.accountConversationTotal,
            userProjectTotal: profileMetrics.userData.userProjectTotal,
            userMailboxTotal: profileMetrics.userData.userMailboxTotal,
            isAccountCreator: profileMetrics.userData.isAccountCreator,
            isAppSumoAccount: profileMetrics.userData.isAppSumoAccount,
            billingTerm: profileMetrics.userData.billingTerm,
            planName: profileMetrics.userData.planName,
            planTier: profileMetrics.userData.planTier,
            planType: profileMetrics.userData.planType,
            accountRole: profileMetrics.userData.role,
            firstName: profileMetrics.userData.firstName,
            lastName: profileMetrics.userData.lastName,
            displayName:
              profileMetrics.userData.firstName +
              ' ' +
              profileMetrics.userData.lastName,
            webexEmbeddedAppEnabled: profile.fromWebex ? 1 : 0,
          })
          // @ts-ignore
          window.dataLayer.push({ event: 'dataLayerVarsLoaded' })
          // @ts-ignore
          window.dataLayer.push({ event: 'dataLayerAndAppcuesLoaded' })
        } else {
          // @ts-ignore
          window.Appcues.anonymous()
        }
        useAppStore().isLoading = false
      } catch (error) {
        errorHandler.emit(error, {})
        if (error.status !== 402) {
          useAppStore().isLoading = false
        }
      }
    }

    // Send Webex share URL to GTM, if applicable...
    // Gotcha: Because the webex initialization is async (and GTM
    // doesn't support ECMAScript), I do a setInterval() to load
    // WebEx. This usually means the very first time we push the 'newRouteForWebex'
    // event which uses the webex js obj to pass the newUrl, it fails since
    // WebEx's JS object hasn't initalized and there is no wait period between the
    // datalayer push above and this call below. Hence, you may see the "Open Together" button
    // only show up starting on the second route change (when WebEx has presumably finished loading).
    if (useProfileStore().data.fromWebex) {
      const newUrl = window.location.origin + to.fullPath
      // @ts-ignore
      window.dataLayer.push({
        event: 'newRouteForWebex',
        currentWebAppRoute: newUrl,
      })
    }

    // Start notification polling, if needed...
    if (useAppStore().notificationIntervalID === null) {
      await useProfileStore().notificationsFetch()
      const newIntervalID = setInterval(async () => {
        await useProfileStore().notificationsFetch()
      }, 60000)
      useAppStore().notificationIntervalID = newIntervalID
    }
  }
}

const isAdminOwnerOnly = (to: any) =>
  to.matched.some((record: any) => record.meta.adminOwnerOnly)

const isAccountMembershipAgnostic = (to: any) =>
  to.matched.some((record: any) => record.meta.accountMembershipAgnostic)
